import React, { useState } from 'react';
import { Button } from '@asigna/kit';
import { makeStyles } from '@material-ui/core';
import { bytesToHex } from '@stacks/common';
import { createStacksPublicKey, deserializeTransaction } from '@stacks/transactions';
import { LedgerError } from '@zondax/ledger-stacks';

import { CommonLedgerDeviceInlineWarnings } from '../../../hooks/ledger/ledger-inline-warnings';
import {
  connectLedgerStacksApp,
  getStacksAppVersion,
  isVersionOfLedgerStacksAppWithContractPrincipalBug,
  signLedgerTransaction,
  signTransactionWithSignature,
} from '../../../hooks/ledger/stacks-ledger-utils';
import { useVerifyMatchingLedgerStacksPublicKey } from '../../../hooks/ledger/use-verify-matching-stacks-public-key';
import { getLedgerStorage } from '../../../store/ledger/ledger-storage';
import { getStorageKeysForNetwork } from '../../../utils/LocalstorageUtils';
import { TransactionCommonDialog } from '../TransactionDialog/TransactionCommonDialog';

interface TransactionCommonProps {
  open: boolean;
  unsignedTx: string;
  onClose: () => void;
  onSuccess: (signedTx: string) => any;
}

const useStyles = makeStyles(() => ({
  wrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    marginTop: '32px',
    gap: '8px',
    marginBottom: '32px',
    // input: {
    //   color: '#949494',
    //   background: '#292a2f',
    //   border: '1px solid #34353b',
    //   '::placeholder': { color: '#949494' },
    //   ':focus': { background: '#292a2f', border: '1px solid #7F53FE' },
    // },
  },
  container: {
    display: 'grid',
    gap: 8,
  },
  inviteCode: {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '20px',
  },
  error: {
    color: '#D92B2B',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '18px',
  },
  loader: {
    width: 50,
    height: 50,
    margin: '50px auto',
  },
}));

export const LedgerSignTransactionDialog = (props: TransactionCommonProps) => {
  const { open, onClose, onSuccess, unsignedTx } = props;
  const [hasError] = useState(false);
  const [signMessageResponse, setSignMessageResponse] = useState<any>();
  const [awaitingSignature, setAwaitingSignature] = useState(false);
  const verifyLedgerPublicKey = useVerifyMatchingLedgerStacksPublicKey();

  const handleSignTx = async () => {
    let stacksApp: Awaited<ReturnType<typeof connectLedgerStacksApp>> | null = null;
    try {
      stacksApp = await connectLedgerStacksApp();
      const ledgerStorage = getLedgerStorage();
      const ledgerInfo = ledgerStorage.getLedgerInfo();
      if (!ledgerInfo) {
        throw new Error('No ledger info found');
      }

      const versionInfo = await getStacksAppVersion(stacksApp);
      setSignMessageResponse(versionInfo);
      setAwaitingSignature(true);

      if (versionInfo.deviceLocked) {
        throw new Error('Device locked');
      }

      if (isVersionOfLedgerStacksAppWithContractPrincipalBug(versionInfo)) {
        throw new Error('Ledger stacks app version is too low');
      }

      await verifyLedgerPublicKey(stacksApp);
      const deserializedTx = deserializeTransaction(unsignedTx);
      deserializedTx.appendPubkey(createStacksPublicKey(ledgerInfo.publicKey));
      const txWithKey = bytesToHex(deserializedTx.serialize());
      const derivation = localStorage.getItem(getStorageKeysForNetwork().derivationPath);

      if (!derivation) {
        throw new Error('No derivation saved');
      }

      const resp = await signLedgerTransaction(stacksApp)(
        Buffer.from(txWithKey, 'hex'),
        derivation
      );

      if (resp.returnCode === LedgerError.DataIsInvalid) {
        throw new Error(LedgerError.DataIsInvalid.toString());
      }

      if (resp.returnCode === LedgerError.TransactionRejected) {
        throw new Error(LedgerError.TransactionRejected.toString());
      }

      if (resp.returnCode !== LedgerError.NoErrors) {
        throw new Error('Some other error');
      }

      const signedTx = signTransactionWithSignature(txWithKey, resp.signatureVRS);

      onSuccess(bytesToHex(signedTx.serialize()));
    } catch (e) {
      console.error(e);
      setAwaitingSignature(false);
    } finally {
      if (stacksApp) {
        try {
          await stacksApp.transport.close();
        } catch {
          // ignore close errors
        }
      }
    }
  };

  const classes = useStyles();

  return (
    <TransactionCommonDialog
      open={open}
      title="Sign transaction"
      button={
        <Button
          fullWidth
          onClick={() => handleSignTx()}
          disabled={awaitingSignature}
          message={awaitingSignature ? 'Confirm transaction' : 'Connect'}
          colorType="primary"
        />
      }
      onClose={onClose}
    >
      <div className={classes.wrapper}>
        {awaitingSignature && <>Please sign transaction on your ledger to proceed</>}
        {awaitingSignature ? (
          <img className={classes.loader} src="/loading-gif.gif" alt="" />
        ) : (
          <>
            <CommonLedgerDeviceInlineWarnings
              latestDeviceResponse={signMessageResponse}
              chain="Stacks"
              outdatedLedgerAppWarning={false}
            />
            {hasError && <span className={classes.error}>Error signing transaction</span>}
          </>
        )}
      </div>
    </TransactionCommonDialog>
  );
};
